/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { usePortalSession } from '../../context/PortalContext';
import { useBookingDraft } from '../../context/PortalContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../api/axiosClient';
import toast from '../../utils/toast';
import { motion } from 'framer-motion';
import { widgetPadding, animations, typography } from '../../utils/designTokens';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const getBrowserTimezone = () => 'UTC';

// Status chip component
const StatusChip = ({ status, isDark }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return {
          bg: isDark ? 'bg-yellow-900/20' : 'bg-yellow-100',
          text: isDark ? 'text-yellow-300' : 'text-yellow-800',
          border: isDark ? 'border-yellow-700' : 'border-yellow-200',
          label: 'Pending'
        };
      case 'confirmed':
        return {
          bg: isDark ? 'bg-green-900/20' : 'bg-green-100',
          text: isDark ? 'text-green-300' : 'text-green-800',
          border: isDark ? 'border-green-700' : 'border-green-200',
          label: 'Confirmed'
        };
      case 'completed':
        return {
          bg: isDark ? 'bg-[rgba(125,102,51,.15)]' : 'bg-amber-100',
          text: isDark ? 'text-amber-300' : 'text-amber-800',
          border: isDark ? 'border-[rgba(125,102,51,.35)]' : 'border-amber-200',
          label: 'Completed'
        };
      case 'no_show':
        return {
          bg: isDark ? 'bg-red-900/20' : 'bg-red-100',
          text: isDark ? 'text-red-300' : 'text-red-800',
          border: isDark ? 'border-red-700' : 'border-red-200',
          label: 'No Show'
        };
      case 'cancelled':
        return {
          bg: isDark ? 'bg-gray-900/20' : 'bg-gray-100',
          text: isDark ? 'text-gray-300' : 'text-gray-800',
          border: isDark ? 'border-gray-700' : 'border-gray-200',
          label: 'Cancelled'
        };
      default:
        return {
          bg: isDark ? 'bg-gray-900/20' : 'bg-gray-100',
          text: isDark ? 'text-gray-300' : 'text-gray-800',
          border: isDark ? 'border-gray-700' : 'border-gray-200',
          label: status
        };
    }
  };

  const config = getStatusConfig(status);
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Cancellation Modal Component
const CancellationModal = ({ isOpen, onClose, onConfirm, booking, isDark }) => {
  const { t } = useTranslation('booking');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      setReason('');
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="p-6 rounded-2xl max-w-md w-full mx-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
            <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('booking.cancel_booking_btn')}
          </h3>
        </div>

        {/* Booking Details */}
        {booking && (
          <div className="mb-6 p-4 rounded-xl bg-zinc-100 dark:bg-zinc-800/50 border border-white/10 dark:border-white/10">
            <h4 className="font-medium mb-2 text-zinc-900 dark:text-zinc-100">
              {booking.service_name}
            </h4>
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              with {booking.barber_name}
            </p>
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              {new Date(booking.start_time).toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
              })}
            </p>
          </div>
        )}

        {/* Reason Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">
            {t('booking.cancellation_reason_label')}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please let us know why you're cancelling this appointment..."
            className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500/30 transition-all resize-none"
            rows={3}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClose}
            className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
            disabled={isSubmitting}
          >
            {t('booking.bookings_keep')}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('booking.cancelling')}
              </div>
            ) : (
              t('booking.cancel_booking_btn')
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

// Booking card component for mobile
const BookingCard = ({ booking, onReschedule, onCancel, onAddToCalendar, isDark, salonSettings }) => {
  const { t } = useTranslation('booking');
  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const dateStr = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: salonSettings?.timezone || localStorage.getItem('shopTimezone') || getBrowserTimezone()
    });
    
    return `${weekday}, ${dateStr} at ${timeStr}`;
  };

  const formatDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / (1000 * 60));
    return `${diffMins} min`;
  };

  const canReschedule = () => {
    if (booking.status === 'cancelled' || booking.status === 'completed') return false;
    const now = new Date();
    const bookingStart = new Date(booking.start_time);
    const minRescheduleTime = new Date(now.getTime() + (salonSettings?.client_reschedule_lead_min || 120) * 60000);
    return bookingStart > minRescheduleTime;
  };

  const canCancel = () => {
    if (booking.status === 'cancelled' || booking.status === 'completed') return false;
    const now = new Date();
    const bookingStart = new Date(booking.start_time);
    const minCancelTime = new Date(now.getTime() + (salonSettings?.client_cancel_lead_min || 120) * 60000);
    return bookingStart > minCancelTime;
  };

  return (
    <div className="p-4 rounded-2xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl shadow-lg">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-zinc-900 dark:text-zinc-100">
            {booking.service_name}
          </h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            with {booking.barber_name}
          </p>
        </div>
        <StatusChip status={booking.status} isDark={isDark} />
      </div>

      <div className="text-sm text-zinc-600 dark:text-zinc-300 mb-3">
        <div>{formatDateTime(booking.start_time)}</div>
        <div>{formatDuration(booking.start_time, booking.end_time)} • {salonSettings?.timezone || localStorage.getItem('shopTimezone') || getBrowserTimezone()}</div>
      </div>

      {/* Policy hint */}
      {booking.status !== 'cancelled' && booking.status !== 'completed' && salonSettings && (
        <div className="text-xs mb-3 text-zinc-500 dark:text-zinc-400">
          You can reschedule/cancel up to {Math.floor((salonSettings.client_reschedule_lead_min || 120) / 60)} hours before
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {canReschedule() && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onReschedule(booking)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-white hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {t('booking.reschedule')}
          </motion.button>
        )}
        
        {canCancel() && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onCancel(booking)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-red-600 hover:bg-red-700 text-white"
          >
            {t('booking.cancel')}
          </motion.button>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onAddToCalendar(booking)}
          className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-300 dark:bg-amber-600/20 dark:hover:bg-amber-600/30 dark:text-amber-300 dark:border-amber-500/30"
        >
          {t('booking.bookings_add_to_calendar')}
        </motion.button>
      </div>
    </div>
  );
};

// Booking table row component for desktop
const BookingRow = ({ booking, onReschedule, onCancel, onAddToCalendar, isDark, salonSettings }) => {
  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const dateStr = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: salonSettings?.timezone || localStorage.getItem('shopTimezone') || getBrowserTimezone()
    });
    
    return `${weekday}, ${dateStr} at ${timeStr}`;
  };

  const formatDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / (1000 * 60));
    return `${diffMins} min`;
  };

  const canReschedule = () => {
    if (booking.status === 'cancelled' || booking.status === 'completed') return false;
    const now = new Date();
    const bookingStart = new Date(booking.start_time);
    const minRescheduleTime = new Date(now.getTime() + (salonSettings?.client_reschedule_lead_min || 120) * 60000);
    return bookingStart > minRescheduleTime;
  };

  const canCancel = () => {
    if (booking.status === 'cancelled' || booking.status === 'completed') return false;
    const now = new Date();
    const bookingStart = new Date(booking.start_time);
    const minCancelTime = new Date(now.getTime() + (salonSettings?.client_cancel_lead_min || 120) * 60000);
    return bookingStart > minCancelTime;
  };

  return (
    <motion.tr 
      className="text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors"
      whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
    >
      <td className="py-4 px-4">
        <div>
          <div className="font-medium text-zinc-900 dark:text-zinc-100">
            {booking.service_name}
          </div>
          <div className="text-sm text-zinc-500 dark:text-zinc-400">
            with {booking.barber_name}
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="text-sm text-zinc-800 dark:text-zinc-100">
          {formatDateTime(booking.start_time)}
        </div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          {salonSettings?.timezone || localStorage.getItem('shopTimezone') || getBrowserTimezone()}
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="text-sm text-zinc-800 dark:text-zinc-100">
          {formatDuration(booking.start_time, booking.end_time)}
        </div>
      </td>
      <td className="py-4 px-4">
        <StatusChip status={booking.status} isDark={isDark} />
      </td>
      <td className="py-4 px-4 text-right">
        <div className="flex items-center gap-2 justify-end">
          {canReschedule() && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onReschedule(booking)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-white hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Reschedule
            </motion.button>
          )}
          
          {canCancel() && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onCancel(booking)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-red-600 hover:bg-red-700 text-white"
            >
              Cancel
            </motion.button>
          )}

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onAddToCalendar(booking)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-300 dark:bg-amber-600/20 dark:hover:bg-amber-600/30 dark:text-amber-300 dark:border-amber-500/30"
          >
            Calendar
          </motion.button>
        </div>
      </td>
    </motion.tr>
  );
};

const MyBookingsPage = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const { portalData } = usePortalSession();
  const { updateDraft } = useBookingDraft();
  const navigate = useNavigate();
  
  const [bookings, setBookings] = useState([]);
  const [salonSettings, setSalonSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showCancelled, setShowCancelled] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Load bookings based on active tab
  const loadBookings = async (tab = activeTab, page = 1) => {
    try {
      setLoading(true);
      setError(null);

      let params = {
        page,
        limit: pagination.limit
      };

      if (tab === 'upcoming') {
        params.from = new Date().toISOString().split('T')[0];
        params.status = 'pending,confirmed'; // Only upcoming statuses
      } else if (tab === 'past') {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        params.to = new Date().toISOString().split('T')[0];
        params.status = 'completed,no_show';
      } else if (tab === 'all') {
        // Show all bookings including cancelled if toggle is on
        if (!showCancelled) {
          params.status = 'pending,confirmed,completed,no_show';
        }
      }

      const response = await axios.get('/portal/bookings', { params });
      
      setBookings(response.data.bookings);
      setPagination(response.data.pagination);
      
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setError('Failed to load bookings');
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Load salon settings
  const loadSalonSettings = async () => {
    try {
      const response = await axios.get('/api/salons/1/settings');
      setSalonSettings(response.data);
    } catch (error) {
      console.warn('Failed to load salon settings:', error);
      // Use default values
      setSalonSettings({
        client_cancel_lead_min: 120,
        client_reschedule_lead_min: 120
      });
    }
  };

  useEffect(() => {
    loadSalonSettings();
  }, []);

  useEffect(() => {
    loadBookings();
  }, [activeTab, showCancelled]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    loadBookings(activeTab, newPage);
  };

  const handleReschedule = (booking) => {
    // Pre-fill booking draft with current booking data
    updateDraft({
      barber_user_id: booking.barber_user_id,
      service_id: booking.service_id,
      date: booking.start_time.split('T')[0],
      slotHHMM: booking.start_time.split('T')[1].substring(0, 5),
      start_local: booking.start_time,
      reschedule_booking_id: booking.id
    });
    
    // Navigate to time selection using React Router
    navigate('/app/book/time');
  };

  const handleCancelClick = (booking) => {
    setSelectedBooking(booking);
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async (reason) => {
    try {
      await axios.post(`/portal/bookings/${selectedBooking.id}/cancel`, {
        reason: reason || undefined
      });
      
      toast.success('Booking cancelled successfully');
      loadBookings(); // Refresh the list
      
      // Fire analytics event
      if (window.gtag) {
        window.gtag('event', 'booking_cancelled', {
          booking_id: selectedBooking.id,
          service_id: selectedBooking.service_id,
          barber_id: selectedBooking.barber_user_id
        });
      }
      
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      
      const errorCode = error.response?.data?.error;
      let errorMessage = 'Failed to cancel booking';
      
      switch (errorCode) {
        case 'CLIENT_CANCEL_WINDOW':
          errorMessage = `You can only cancel bookings more than ${Math.floor((salonSettings?.client_cancel_lead_min || 120) / 60)} hours in advance`;
          break;
        case 'BOOKING_NOT_CANCELLABLE':
          errorMessage = 'This booking cannot be cancelled';
          break;
        default:
          errorMessage = error.response?.data?.error || errorMessage;
      }
      
      toast.error(errorMessage);
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const handleAddToCalendar = (booking) => {
    const startDate = new Date(booking.start_time);
    const endDate = new Date(booking.end_time);
    
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Hairchitech//Booking System//EN',
      'BEGIN:VEVENT',
      `UID:${booking.id}@hairchitech.com`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:Hairchitech Appointment - ${booking.service_name}`,
      `DESCRIPTION:Appointment with ${booking.barber_name}\\nService: ${booking.service_name}`,
      `LOCATION:Hairchitech Salon`,
      `STATUS:CONFIRMED`,
      `ORGANIZER:CN=Hairchitech:mailto:bookings@hairchitech.com`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `booking-${booking.id}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Calendar event downloaded');
    
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'calendar_downloaded', {
        booking_id: booking.id,
        source: 'my_bookings'
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <div className="animate-pulse">
            <div className="h-8 w-64 bg-gray-300 rounded mb-2"></div>
            <div className="h-4 w-48 bg-gray-300 rounded"></div>
          </div>
        </div>
        
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className={`p-4 rounded-lg border ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className="animate-pulse">
                <div className="h-4 w-32 bg-gray-300 rounded mb-2"></div>
                <div className="h-3 w-24 bg-gray-300 rounded mb-3"></div>
                <div className="flex space-x-2">
                  <div className="h-6 w-16 bg-gray-300 rounded"></div>
                  <div className="h-6 w-16 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {t('booking.my_bookings_title')}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              Manage your appointments and booking history • All times in {salonSettings?.timezone || localStorage.getItem('shopTimezone') || getBrowserTimezone()}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Card className="mb-6">
        <div className="p-4 md:p-5">
          <div className="border-b border-white/10 dark:border-white/10">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'upcoming', label: 'Upcoming' },
                { key: 'past', label: 'Past' },
                { key: 'all', label: 'All' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? 'text-zinc-900 dark:text-zinc-100'
                      : 'border-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-600'
                  }`}
                  style={activeTab === tab.key ? {
                    borderBottomColor: ACCENT,
                    color: ACCENT
                  } : {}}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </Card>

      {/* Show cancelled toggle for All tab */}
      {activeTab === 'all' && (
        <Card className="mb-6">
          <div className="p-4 md:p-5">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                className="rounded border-white/20 dark:border-white/10 text-[--accent] shadow-sm focus:border-[--accent]/30 focus:ring focus:ring-[--accent]/20 focus:ring-opacity-50 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm"
                style={{ '--accent': ACCENT }}
              />
              <span className="ml-2 text-sm text-zinc-700 dark:text-zinc-300">
                {t('booking.show_cancelled')}
              </span>
            </label>
          </div>
        </Card>
      )}

      {/* Error state */}
      {error && (
        <Card className="mb-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <span className="font-medium text-red-700 dark:text-red-300">{error}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Bookings list */}
      {bookings.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
              <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              {activeTab === 'upcoming' ? 'No upcoming bookings' : 
               activeTab === 'past' ? 'No past bookings' : 'No bookings found'}
            </h3>
            <p className="text-zinc-500 dark:text-zinc-400 mb-6">
              {activeTab === 'upcoming' ? "You don't have any upcoming bookings yet." : 
               activeTab === 'past' ? "You don't have any past bookings." : 
               "No bookings match your current filters."}
            </p>
            {activeTab === 'upcoming' && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg"
                style={{ background: ACCENT }}
              >
                <Link to="/app/book" className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('booking.portal_book_now')}
                </Link>
              </motion.button>
            )}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile view - Cards */}
          <div className="block md:hidden space-y-4">
            {bookings.map(booking => (
              <motion.div
                key={booking.id}
                initial={animations.initial}
                whileHover={animations.hover}
                whileTap={animations.tap}
              >
                <BookingCard
                  booking={booking}
                  onReschedule={handleReschedule}
                  onCancel={handleCancelClick}
                  onAddToCalendar={handleAddToCalendar}
                  isDark={isDark}
                  salonSettings={salonSettings}
                />
              </motion.div>
            ))}
          </div>

          {/* Desktop view - Table */}
          <Card>
            <CardHeader
              title="Bookings"
              subtitle={`${bookings.length} total bookings`}
              icon={
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              }
            />
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                    <th className="py-4 px-4 font-medium">{t('booking.bookings_service')}</th>
                    <th className="py-4 px-4 font-medium">{t('booking.bookings_date_time')}</th>
                    <th className="py-4 px-4 font-medium">{t('booking.bookings_duration')}</th>
                    <th className="py-4 px-4 font-medium">{t('booking.bookings_status')}</th>
                    <th className="py-4 px-4 font-medium text-right">{t('booking.bookings_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 dark:divide-white/10">
                  {bookings.map(booking => (
                    <BookingRow
                      key={booking.id}
                      booking={booking}
                      onReschedule={handleReschedule}
                      onCancel={handleCancelClick}
                      onAddToCalendar={handleAddToCalendar}
                      isDark={isDark}
                      salonSettings={salonSettings}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Card className="mt-6">
              <div className="p-4 md:p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-zinc-600 dark:text-zinc-400">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {t('booking.portal_previous')}
                    </motion.button>
                    
                    <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {t('booking.portal_next')}
                    </motion.button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Cancellation Modal */}
      <CancellationModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancelConfirm}
        booking={selectedBooking}
        isDark={isDark}
      />
    </div>
  );
};

export default MyBookingsPage;
